import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  BookOpen, ChevronDown, ChevronRight, Search, Globe, FileText, Users,
  Settings, Upload, BarChart3, Package, Bot, Calculator, Microscope,
  Sun, ShoppingCart, MapPin, GraduationCap, Edit3, Shield, HelpCircle
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const guides = [
  {
    id: 'getting-started',
    icon: BookOpen,
    title: 'Getting Started',
    badge: 'Start Here',
    badgeColor: 'bg-green-100 text-green-700',
    sections: [
      { title: 'Logging In', content: 'Go to /login and use your admin credentials. Quick-login buttons are available for testing: Admin (admin@readysetstem.org / Admin123!). After logging in, you\'ll see the Admin Dashboard with key metrics.' },
      { title: 'Admin Dashboard Overview', content: 'The dashboard shows: Total Revenue, Active Enrollments, Attendance Rate, and Points Earned. The sidebar on the left has all admin modules. Scroll down to see Enrollment Breakdown and Gamification Stats.' },
      { title: 'Understanding the Sidebar', content: 'The sidebar has these sections:\n• Dashboard — Overview metrics\n• Courses — Add/edit courses\n• Classes — Manage class sections\n• People — Manage users (parents, instructors)\n• Learners — View enrolled students\n• Rewards — Manage gamification\n• Tools — Import data, manage forms\n• Blog — Write blog posts\n• Site Settings — Toggle navigation items\n• Edit Pages — Change website text' },
    ]
  },
  {
    id: 'edit-website',
    icon: Edit3,
    title: 'Editing Website Content',
    badge: 'No Coding Needed',
    badgeColor: 'bg-blue-100 text-blue-700',
    sections: [
      { title: 'How to Edit Text on the Website', content: '1. Go to Admin → Edit Pages (in the sidebar)\n2. Click a page tab (Homepage, About, Contact, etc.)\n3. Find the text you want to change\n4. Click the pencil icon ✏️ next to the text\n5. Type your new text\n6. Click "Save All Changes" (top right)\n7. The change is live immediately on the website!' },
      { title: 'Editing Statistics & Numbers', content: 'Stats like "12,000+ Learners" appear with a bar chart icon. Click to edit the number. These are shown in large bold text so you can easily find them. Always click "Save All Changes" when done.' },
      { title: 'What Can You Edit?', content: '• Homepage: Hero title, subtitle, stats line, badge text, button labels\n• Homepage: Social proof badges and rating\n• About Page: Hero description, mission statement, vision statement\n• Our Story: Story subtitle, founder quote\n• Contact: Email, phone, address' },
    ]
  },
  {
    id: 'navigation',
    icon: Globe,
    title: 'Managing Navigation',
    badge: 'Show/Hide Menu Items',
    badgeColor: 'bg-purple-100 text-purple-700',
    sections: [
      { title: 'How to Show/Hide Navigation Items', content: '1. Go to Admin → Site Settings\n2. You\'ll see all navigation items with toggle switches\n3. Turn OFF a toggle to hide that item from the public website\n4. Turn ON to show it again\n5. Changes are immediate — no save button needed!' },
      { title: 'Managing Subcategories', content: 'Below the main navigation items, you\'ll see sections for each dropdown menu (Coding, Math, Science, etc.). Each subcategory has its own toggle. For example, you can hide "MATHCOUNTS" from the Math dropdown while keeping everything else visible.' },
      { title: 'When to Use This', content: '• Hiding a program that\'s not ready yet (e.g., Summer Camps before registration opens)\n• Removing a subcategory that\'s sold out\n• Temporarily hiding a partner program\n• Simplifying the menu for a specific season' },
    ]
  },
  {
    id: 'import-courses',
    icon: Upload,
    title: 'Importing Courses',
    badge: 'Bulk Upload',
    badgeColor: 'bg-orange-100 text-orange-700',
    sections: [
      { title: 'Step-by-Step Course Import', content: '1. Go to Admin → Tools → Import\n2. Download the "Course Import Template" (blue button)\n3. Open it in Excel or Google Sheets\n4. Replace the sample data with your real courses\n5. Keep the header row exactly as it is!\n6. Save as CSV\n7. Go back to Admin → Tools → Import → File Upload tab\n8. Upload your CSV file\n9. Done! Courses appear in the catalog immediately.' },
      { title: 'Required Fields', content: 'Every course MUST have:\n• title — Course name (must be unique)\n• description — What parents see\n• skill_level — beginner, intermediate, or advanced\n• duration_minutes — How long each class is\n• grade_bands — Who it\'s for (e.g., K-2, 3-5)\n• topic_tags — Categories (e.g., Scratch, Math, AMC)\n• price — Monthly price in dollars (no $ sign)' },
      { title: 'IMPORTANT: Topic Tags', content: 'Topic tags control which dropdown menu a course appears under. You MUST use these exact values:\n\nCoding: Scratch, Python, Web, AI, Game, Cybersecurity\nMath: Math, AMC, MATHCOUNTS, Olympiad\nScience: Science Olympiad, Biomedical, Chemistry, Environmental\nEngineering: CAD, 3D, Mechanical, Architecture\n\nYou can use multiple tags separated by commas.' },
      { title: 'Importing Summer Camps', content: '1. Go to Admin → Tools → Import → Summer Camps tab\n2. Download the "Summer Camp Template" (orange button)\n3. Fill in your camp data including week numbers, dates, and schedule type\n4. Upload via CSV or paste a Google Sheets URL\n5. Camp schedule types: full_day (9AM-3PM), half_am (9AM-12PM), half_pm (12PM-3PM), extended (8AM-5PM)' },
      { title: 'Re-importing Updates (No Duplicates)', content: 'If you import a course with the same title as an existing course, it UPDATES the existing one instead of creating a duplicate. This means you can safely re-import your spreadsheet after making changes.' },
    ]
  },
  {
    id: 'import-sawyer',
    icon: Users,
    title: 'Importing from Sawyer',
    badge: 'Migration',
    badgeColor: 'bg-teal-100 text-teal-700',
    sections: [
      { title: 'How to Import Sawyer Data', content: '1. Log into your Sawyer admin dashboard\n2. Go to Reports → Booking Report\n3. Export as CSV or publish to Google Sheets\n4. Go to Admin → Tools → Import → Sawyer Import tab\n5. Paste the Google Sheets URL OR upload the CSV\n6. Click "Import from Sawyer"\n7. The system automatically creates: Parent accounts, Child profiles, Course enrollments' },
      { title: 'What Gets Imported', content: '• Customer Email/Name → Parent user account (password: Welcome123!)\n• Child Name → Learner profile linked to parent\n• Activity Name → Matched to existing course or created as placeholder\n• Booking → Enrollment record\n• Canceled bookings are automatically skipped' },
      { title: 'After Import', content: 'Parents can log in with their email and the default password "Welcome123!" — they should change it on first login. Children appear under the parent\'s "My Learners" section.' },
    ]
  },
  {
    id: 'manage-courses',
    icon: GraduationCap,
    title: 'Managing Courses & Classes',
    badge: 'Day-to-Day',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    sections: [
      { title: 'Adding a Single Course', content: '1. Go to Admin → Courses\n2. Click "+ Add Course" (top right)\n3. Fill in the course details\n4. Click Save\n5. Then go to Admin → Classes to add a class section for this course (schedule, location, instructor)' },
      { title: 'Editing a Course', content: 'Go to Admin → Courses → click the pencil icon ✏️ on any course. You can edit the title, description, skill level, grade bands, topics, and more. The "Standards" tab lets you manage state standards.' },
      { title: 'Managing Class Sections', content: 'A "Section" is a specific class instance — with a schedule, instructor, location, and price. One course can have multiple sections (e.g., Monday 4PM and Saturday 10AM). Go to Admin → Classes to manage these.' },
    ]
  },
  {
    id: 'manage-people',
    icon: Users,
    title: 'Managing Users & Learners',
    badge: 'People',
    badgeColor: 'bg-pink-100 text-pink-700',
    sections: [
      { title: 'Viewing All Users', content: 'Go to Admin → People. You\'ll see all users with their role (admin, instructor, parent, partner). Use the tabs at the top to view Applications, Onboarding, Scheduling, and Certificates.' },
      { title: 'Adding a User', content: 'Click "+ Add User" and fill in email, name, phone, and select a role. The user will receive a welcome email (if email is configured) or you can share their login credentials manually.' },
      { title: 'Viewing Learners', content: 'Go to Admin → Learners to see all enrolled students with their grade, school, enrollment count, and points. Click "View" on any learner to see their full profile and enrollment history.' },
    ]
  },
  {
    id: 'manage-blog',
    icon: FileText,
    title: 'Writing Blog Posts',
    badge: 'Content',
    badgeColor: 'bg-indigo-100 text-indigo-700',
    sections: [
      { title: 'Creating a Blog Post', content: '1. Go to Admin → Blog\n2. Click "+ New Post" (top right)\n3. Write your title, content, and select a category\n4. Add a featured image URL (optional)\n5. Set status to "Published" to make it live\n6. Click Save' },
      { title: 'Blog Categories', content: 'Available categories: announcement, news, success_story, event. These show as colored badges on each post. Parents can filter blog posts by category on the public blog page.' },
    ]
  },
  {
    id: 'marketplace',
    icon: ShoppingCart,
    title: 'Managing Partner Programs',
    badge: 'Marketplace',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    sections: [
      { title: 'How the Marketplace Works', content: 'External education partners (Mathnasium, RSM, Sylvan) can list their programs on your platform. Three models:\n• Referral (10%) — we link to their site, earn commission per referral\n• Embedded (15%) — parents enroll on our site, we handle payment\n• White-Label (20-30%) — partner curriculum under our brand' },
      { title: 'Reviewing Partner Listings', content: 'All new partner listings require admin approval before going live. Go to the marketplace listings and use the Review action to approve, reject, or suspend listings. You can add admin notes explaining your decision.' },
      { title: 'Protecting Against Competition', content: 'Each partner has a "compete_restriction" field where you can specify which subjects/grades they cannot compete with your own courses. For example, you might restrict a math partner from listing courses that overlap with your AMC prep program.' },
    ]
  },
  {
    id: 'venues',
    icon: MapPin,
    title: 'Managing Venues & Bookings',
    badge: 'Locations',
    badgeColor: 'bg-cyan-100 text-cyan-700',
    sections: [
      { title: 'Integration Modes', content: 'Each venue partner has an integration mode:\n• Full — Uses our booking system, calendar, and payments\n• Calendar Only — Can view their schedule but manages bookings externally\n• Manual — We track everything internally, partner doesn\'t use our system\n• External — Partner uses their own system (e.g., library booking system)\n\nSet this based on each partner\'s preference.' },
      { title: 'Checking Venue Availability', content: 'Before booking a venue for a camp, use the availability check feature. It will tell you if the space is free or if there are conflicts with existing bookings or blackout dates.' },
    ]
  },
  {
    id: 'rewards',
    icon: Package,
    title: 'Rewards & Gamification',
    badge: 'Engagement',
    badgeColor: 'bg-amber-100 text-amber-700',
    sections: [
      { title: 'How Points Work', content: 'Students earn points through:\n• Class attendance (10-25 pts)\n• Assignment completion\n• Perfect attendance streaks\n• Helping classmates\n• Coding challenge completions\n\nParents can see point balances in their dashboard. Students redeem points in the Rewards Store.' },
      { title: 'Managing Rewards', content: 'Go to Admin → Rewards to see all available rewards. Rewards are age-gated:\n• Young (5-9): Trading cards, Squishmallows, bracelet kits\n• Tween (10-13): Roblox/Minecraft gift cards, art supplies\n• Teen (14-18): Fortnite/Steam cards, tech items\n\nYou can add new rewards, adjust point costs, and manage inventory.' },
    ]
  },
];

export default function AdminGuidePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGuide, setExpandedGuide] = useState('getting-started');
  const [expandedSection, setExpandedSection] = useState({});

  const toggleSection = (guideId, sectionIdx) => {
    const key = `${guideId}-${sectionIdx}`;
    setExpandedSection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredGuides = searchQuery
    ? guides.filter(g =>
        g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.sections.some(s =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : guides;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <HelpCircle className="w-6 h-6" /> Admin Help Guide
        </h1>
        <p className="text-muted-foreground">Step-by-step instructions for managing the Ready Set STEM platform. No technical knowledge required.</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          className="pl-10"
          placeholder="Search guides... (e.g., 'import courses', 'edit website')"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Guide List */}
      <div className="space-y-4">
        {filteredGuides.map((guide) => {
          const Icon = guide.icon;
          const isExpanded = expandedGuide === guide.id;
          return (
            <Card key={guide.id} className={isExpanded ? 'border-rsc-blue' : ''}>
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedGuide(isExpanded ? null : guide.id)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <div className="w-10 h-10 rounded-lg bg-rsc-blue/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-rsc-blue" />
                    </div>
                    {guide.title}
                    <Badge className={`${guide.badgeColor} text-xs`}>{guide.badge}</Badge>
                  </CardTitle>
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
              </CardHeader>
              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {guide.sections.map((section, idx) => {
                      const sKey = `${guide.id}-${idx}`;
                      const isOpen = expandedSection[sKey] !== false; // default open
                      return (
                        <div key={idx} className="border rounded-lg overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                            onClick={() => toggleSection(guide.id, idx)}
                          >
                            <span className="font-semibold text-sm">{section.title}</span>
                            {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          </button>
                          {isOpen && (
                            <div className="px-4 pb-3 text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                              {section.content}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
